/**
 * Auth gate: guests browse the app; login is requested only on execute.
 *
 * `requireAuth()` resolves true if already signed in, or after a successful
 * login in the modal. Resolves false if the visitor closes the modal.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useAuth } from './AuthContext';
import AuthGateModal from '../components/AuthGateModal';

const AuthGateContext = createContext(null);

/**
 * Hook to open the login gate before an expensive CV action.
 *
 * @returns {Function} requireAuth — Promise<boolean>
 */
export const useRequireAuth = () => {
  const context = useContext(AuthGateContext);
  if (!context) {
    throw new Error('useRequireAuth must be used within an AuthGateProvider');
  }
  return context.requireAuth;
};

/**
 * Auth gate provider. Must sit inside AuthProvider and Router.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export const AuthGateProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const resolverRef = useRef(null);

  const settle = useCallback((ok) => {
    if (resolverRef.current) {
      resolverRef.current(ok);
      resolverRef.current = null;
    }
    setOpen(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated && resolverRef.current) {
      settle(true);
    }
  }, [isAuthenticated, settle]);

  const requireAuth = useCallback(() => {
    if (isAuthenticated) {
      return Promise.resolve(true);
    }
    setOpen(true);
    return new Promise((resolve) => {
      if (resolverRef.current) {
        resolverRef.current(false);
      }
      resolverRef.current = resolve;
    });
  }, [isAuthenticated]);

  const close = useCallback(() => {
    settle(false);
  }, [settle]);

  const value = useMemo(() => ({ requireAuth, open, close }), [requireAuth, open, close]);

  return (
    <AuthGateContext.Provider value={value}>
      {children}
      <AuthGateModal open={open} onClose={close} />
    </AuthGateContext.Provider>
  );
};

AuthGateProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
